/**
 * Color Utility Functions
 *
 * Utilities for color manipulation, contrast detection, and derivation
 */

/**
 * Convert hex color to RGB object
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Convert RGB to hex color
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => {
    const hex = Math.max(0, Math.min(255, Math.round(n))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Calculate relative luminance of a color (0-1)
 * Used for WCAG contrast calculations
 */
export function getLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;

  const { r, g, b } = rgb;
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Check if a color is light (returns true) or dark (returns false)
 */
export function isLightColor(hex: string): boolean {
  return getLuminance(hex) > 0.5;
}

/**
 * Get appropriate contrast text color (black or white) for a background
 */
export function getContrastText(backgroundColor: string): string {
  return isLightColor(backgroundColor) ? '#111827' : '#ffffff';
}

/**
 * Darken a color by a percentage (0-100)
 */
export function darken(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const factor = 1 - percent / 100;
  return rgbToHex(rgb.r * factor, rgb.g * factor, rgb.b * factor);
}

/**
 * Lighten a color by a percentage (0-100)
 */
export function lighten(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const factor = percent / 100;
  return rgbToHex(
    rgb.r + (255 - rgb.r) * factor,
    rgb.g + (255 - rgb.g) * factor,
    rgb.b + (255 - rgb.b) * factor
  );
}

/**
 * Adjust color brightness - positive = lighter, negative = darker
 */
export function adjustBrightness(hex: string, amount: number): string {
  return amount > 0 ? lighten(hex, amount) : darken(hex, Math.abs(amount));
}

/**
 * Mix two colors together
 */
export function mixColors(color1: string, color2: string, weight: number = 50): string {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  if (!rgb1 || !rgb2) return color1;

  const w = weight / 100;
  return rgbToHex(
    rgb1.r * w + rgb2.r * (1 - w),
    rgb1.g * w + rgb2.g * (1 - w),
    rgb1.b * w + rgb2.b * (1 - w)
  );
}

/**
 * Create a subtle background variant (for cards, sections)
 */
export function createSubtleBackground(baseColor: string, isDarkMode: boolean = false): string {
  if (isDarkMode) {
    return lighten(baseColor, 5);
  }
  return isLightColor(baseColor) ? darken(baseColor, 3) : lighten(baseColor, 90);
}

/**
 * Create hover state color
 */
export function createHoverColor(baseColor: string): string {
  return isLightColor(baseColor) ? darken(baseColor, 10) : lighten(baseColor, 10);
}
